"use client"

import { css } from "@emotion/css"
import styled from "@emotion/styled"
import { MagnifyingGlass } from "@vectopus/atlas-icons-react"
import Link from "next/link"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { VisuallyHidden } from "react-aria"
import { useTranslation } from "react-i18next"
import { useDebounce } from "use-debounce"

import {
  searchPagesWithPhrase,
  searchPagesWithWords,
} from "@/generated/course-material-api/sdk.generated"
import type { PageSearchResult } from "@/generated/course-material-api/types.generated"
import { isAbortError } from "@/shared-module/common/errors/AppApiError"
import { normalizeErrorForDisplay } from "@/shared-module/common/errors/normalizeErrorForDisplay"
import { baseTheme } from "@/shared-module/common/styles"
import { respondToOrLarger } from "@/shared-module/common/styles/respond"
import { coursePageRoute } from "@/shared-module/common/utils/routes"
import { Button, Dialog, Spinner } from "@/shared-module/components"

import useSearchPagesLiveRegion from "./useSearchPagesLiveRegion"

const HeaderBar = styled.div`
  display: flex;
  padding: 0.75rem 0;
  align-items: center;
  gap: 0.5rem;
  h1 {
    font-size: 1.25rem;
    margin-bottom: 0;
  }
`

const SearchContainer = styled.div<{ $hasContent: boolean }>`
  overflow: hidden;
  width: 100%;
  height: ${(props) => (props.$hasContent ? "700px" : "80px")};
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  transition: height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`

const SearchInputContainer = styled.div`
  position: relative;
  flex-shrink: 0;
  /* Clears the dialog's floating close button. */
  padding: 0 3.5rem 0 1rem;
`

const ResultsContainer = styled.div<{ $hasResults: boolean }>`
  margin-top: 1rem;
  padding: 4px 1rem;
  overflow-y: auto;
  max-height: calc(90vh - 90px);
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 2rem 0;
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  color: ${baseTheme.colors.gray[500]};
  text-align: center;
  transition: padding 0.2s ease;
`

const ResultCard = styled.div`
  /* Positioning context for the stretched heading link, which makes the whole card clickable. */
  position: relative;
  background: #ffffff;
  margin-bottom: 0.5rem;
  padding: 1rem;
  border-radius: 6px;
  transition: all 0.2s ease;
  border: 1px solid ${baseTheme.colors.gray[100]};

  &:hover,
  &:focus-within {
    background: ${baseTheme.colors.green[100]};
    border-color: ${baseTheme.colors.green[200]};
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  }

  .chapter-name {
    font-size: 0.75rem;
    color: ${baseTheme.colors.gray[500]};
    margin: 0 0 0.25rem;
    line-height: 1.4;
  }

  h2 {
    font-size: 1rem;
    margin: 0 0 0.25rem;
    line-height: 1.4;

    ${respondToOrLarger.md} {
      font-size: 1.125rem;
    }
  }

  p {
    font-size: 0.875rem;
    margin: 0;
    line-height: 1.5;
    color: ${baseTheme.colors.gray[600]};

    ${respondToOrLarger.md} {
      font-size: 0.875rem;
    }
  }
`

const ResultHeadingLink = styled(Link)`
  text-decoration: none;
  color: unset;
  display: block;

  /* Stretched link: extends the hit area over the whole card while the accessible name stays the
     heading text. Safe only because the card has no other interactive elements to shadow. */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
  }

  &:focus-visible {
    outline: 2px solid ${baseTheme.colors.green[500]};
    outline-offset: 2px;
  }
`

const StyledInput = styled.input`
  display: flex;
  background: #ffffff;
  width: 100%;
  padding: 0 3.5rem;
  height: 56px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  border: none;
  outline: 1px solid ${baseTheme.colors.gray[200]};
  margin-right: 0.5rem;
  font-size: 1rem;
  transition: all 0.2s ease;

  &:focus {
    outline: 2px solid ${baseTheme.colors.green[400]};
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
  }
`

const SearchIcon = styled(MagnifyingGlass)`
  position: absolute;
  left: 1.5rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${baseTheme.colors.gray[400]};
  transition: color 0.2s ease;
`

const StyledIcon = css`
  :hover {
    cursor: pointer;
  }
  &:focus-visible {
    outline: 2px solid ${baseTheme.colors.green[500]};
    outline-offset: 2px;
  }
`

const KEYS = {
  SHORTCUT: "k",
}

const SEARCH_BUTTON_ID = "search-for-pages-button"

export interface SearchButtonProps {
  courseId?: string | null
  organizationSlug?: string | null
}

const SearchButton: React.FC<SearchButtonProps> = ({ courseId, organizationSlug }) => {
  const { t } = useTranslation()
  const { t: tErrors } = useTranslation("errors")
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState<string>("")
  const [debouncedQuery] = useDebounce(query, 200)
  const [phraseSearchResults, setPhraseSearchResults] = useState<PageSearchResult[] | null>(null)
  const [wordSearchResults, setWordSearchResults] = useState<PageSearchResult[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const abortControllerRef = useRef<AbortController>(new AbortController())
  const inputRef = useRef<HTMLInputElement>(null)

  const combinedResults = useMemo(() => {
    if (phraseSearchResults === null || wordSearchResults === null) {
      return null
    }
    const pages = [...phraseSearchResults]
    wordSearchResults.forEach((pageWithWords) => {
      if (pages.some((p) => p.id === pageWithWords.id)) {
        return
      }
      pages.push(pageWithWords)
    })
    return pages
  }, [phraseSearchResults, wordSearchResults])

  const hasNoResults = useMemo(() => {
    return combinedResults !== null && combinedResults.length === 0 && debouncedQuery.trim() !== ""
  }, [combinedResults, debouncedQuery])

  const hasContent = useMemo(() => {
    return (
      isLoading || !!error || debouncedQuery.trim() !== "" || (combinedResults?.length ?? 0) > 0
    )
  }, [isLoading, error, debouncedQuery, combinedResults])

  const liveRegionMessage = useSearchPagesLiveRegion({
    searchQuery: debouncedQuery,
    isLoading,
    isError: error !== null,
    resultCount: combinedResults?.length ?? null,
  })

  useEffect(() => {
    async function innerFunction() {
      abortControllerRef.current?.abort()
      abortControllerRef.current = new AbortController()

      if (!debouncedQuery || debouncedQuery.trim() === "") {
        setPhraseSearchResults([])
        setWordSearchResults([])
        setIsLoading(false)
        return
      }

      if (!courseId) {
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const [pagesWithPhrase, pagesWithWords] = await Promise.all([
          searchPagesWithPhrase({
            body: { query: debouncedQuery },
            path: {
              course_id: courseId,
            },
            signal: abortControllerRef.current.signal,
          }),
          searchPagesWithWords({
            body: { query: debouncedQuery },
            path: {
              course_id: courseId,
            },
            signal: abortControllerRef.current.signal,
          }),
        ])
        setPhraseSearchResults(pagesWithPhrase)
        setWordSearchResults(pagesWithWords)
      } catch (e: unknown) {
        if (isAbortError(e)) {
          return
        }
        const parsed = normalizeErrorForDisplay(e, tErrors)
        setError(parsed.message ?? parsed.title)
      } finally {
        setIsLoading(false)
      }
    }
    innerFunction()

    return () => {
      abortControllerRef.current?.abort()
    }
  }, [courseId, debouncedQuery, tErrors])

  useEffect(() => {
    const controller = new AbortController()
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === KEYS.SHORTCUT) {
        e.preventDefault()
        setOpen(true)
      }
    }

    window.addEventListener("keydown", handleKeyDown, { signal: controller.signal })

    return () => {
      controller.abort()
    }
  }, [])

  const closeModal = () => {
    setOpen(false)
  }

  const openModal = () => {
    setOpen(true)
  }

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus()
    }
  }, [open])

  const handleResultClick = () => {
    setOpen(false)
    setQuery("")
    setPhraseSearchResults([])
    setWordSearchResults([])
  }

  if (!courseId || !organizationSlug) {
    return null
  }

  return (
    <>
      <Button
        domProps={{ tabIndex: 0, id: SEARCH_BUTTON_ID, "aria-hidden": false }}
        className={StyledIcon}
        aria-label={t("button-label-search-for-pages")}
        size="small"
        variant="icon"
        onClick={openModal}
      >
        <MagnifyingGlass size={16} weight="bold" />
      </Button>
      <Dialog open={open} onClose={closeModal} padding="none" aria-label={t("title-search-dialog")}>
        <SearchContainer $hasContent={hasContent}>
          {/* oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- VisuallyHidden wrapper with role=status; <output> drops the styling */}
          <VisuallyHidden aria-live="polite" aria-atomic role="status">
            {liveRegionMessage}
          </VisuallyHidden>
          <SearchInputContainer>
            <HeaderBar>
              <SearchIcon size={20} weight="bold" />
              <StyledInput
                ref={inputRef}
                value={query}
                onChange={(e) => {
                  setError(null)
                  setQuery(e.target.value)
                }}
                placeholder={t("search-field-placeholder")}
              />
            </HeaderBar>
          </SearchInputContainer>

          <ResultsContainer $hasResults={!!debouncedQuery || isLoading}>
            {error && (
              <div
                className={css`
                  padding: 1rem;
                  background-color: ${baseTheme.colors.red[100]};
                  border-left: 4px solid ${baseTheme.colors.red[500]};
                  color: ${baseTheme.colors.red[700]};
                  margin-bottom: 1rem;
                  border-radius: 4px;
                `}
              >
                {error}
              </div>
            )}

            {/* Decorative: useSearchPagesLiveRegion above already announces the searching state. */}
            {isLoading && (
              <LoadingContainer>
                <Spinner size="md" />
              </LoadingContainer>
            )}

            {hasNoResults && (
              <EmptyState>
                <MagnifyingGlass size={48} weight="thin" />
                <h3>{t("no-results-found")}</h3>
                <p>{t("try-different-search-terms")}</p>
              </EmptyState>
            )}

            {!isLoading &&
              combinedResults?.map((result) => {
                const urlPath = result.url_path.startsWith("/")
                  ? result.url_path
                  : `/${result.url_path}`
                const pathSegments = urlPath.split("/").filter(Boolean)
                const courseSlug = pathSegments[0] ?? ""
                const relativePathWithSlash =
                  pathSegments.length > 1 ? `/${pathSegments.slice(1).join("/")}` : "/"
                return (
                  <ResultCard key={result.id}>
                    <h2
                      className={css`
                        font-size: 1.5rem;
                        b {
                          text-decoration: underline;
                        }
                      `}
                    >
                      <ResultHeadingLink
                        href={coursePageRoute(organizationSlug, courseSlug, relativePathWithSlash)}
                        onClick={handleResultClick}
                        dangerouslySetInnerHTML={{
                          __html: result.title_headline ?? "",
                        }}
                      />
                    </h2>
                    {result.chapter_name !== null &&
                      result.chapter_name !== undefined &&
                      result.chapter_name !== "" && (
                        <div
                          className={css`
                            font-size: 0.75rem;
                            color: ${baseTheme.colors.gray[500]};
                            margin: 0 0 0.25rem;
                            line-height: 1.4;
                          `}
                        >
                          {result.chapter_name}
                        </div>
                      )}
                    {result.content_headline && (
                      <p
                        className={css`
                          color: #5a5757;
                        `}
                        dangerouslySetInnerHTML={{
                          __html: result.content_headline,
                        }}
                      />
                    )}
                  </ResultCard>
                )
              })}
          </ResultsContainer>
        </SearchContainer>
      </Dialog>
    </>
  )
}

export default SearchButton
